import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Calendar from "react-calendar";
import { format, isToday } from "date-fns";
import { useTranslation } from "react-i18next";
import "react-calendar/dist/Calendar.css";
import Trash from "../src/images/trash.png"

const ScheduleTab = () => {
  const { t, i18n } = useTranslation();
  const coachLN = localStorage.getItem("coachLN");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [consultations, setConsultations] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [consultationTypes, setConsultationTypes] = useState({});
  const [reservingSlot, setReservingSlot] = useState(null);
  const [reservationReason, setReservationReason] = useState("");
  const [isReserving, setIsReserving] = useState(false);
  const [breaks, setBreaks] = useState([]);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [consultationNotes, setConsultationNotes] = useState({});

  const fetchConsultations = async (date) => {
    const formattedDate = format(date, "yyyy-MM-dd");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000/"}consultations/booked/${coachLN}/${formattedDate}`
      );
      const data = await res.json();
      if (res.ok) {
        const sortedConsultations = data.sort((a, b) => {
          const timeA = new Date(`1970-01-01T${a.time}:00`);
          const timeB = new Date(`1970-01-01T${b.time}:00`);
          return timeA - timeB;
        });
        setConsultations(sortedConsultations);
      } else {
        console.error("Error fetching consultations:", data);
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const fetchAvailabilities = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000/"}availabilities/coach/${coachLN}`
      );
      const data = await res.json();
      if (res.ok) {
        const sortedAvailabilities = data.map(avail => ({
          ...avail,
          slots: avail.slots.sort((a, b) => {
            const timeA = new Date(`1970-01-01T${a.time}:00`);
            const timeB = new Date(`1970-01-01T${b.time}:00`);
            return timeA - timeB;
          })
        }));
        setAvailabilities(sortedAvailabilities);
      } else {
        console.error("Error fetching availabilities:", data);
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };
  
  const fetchConsultationTypes = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:8000/"}consultations`);
      const data = await res.json();
      if (res.ok) {
        const typesMap = {};
        data.forEach((type) => {
          typesMap[type._id] = {
            name: type.name,
            realDuration: type.realDuration,
          };
        });
        setConsultationTypes(typesMap);
      } else {
        console.error("Error fetching consultation types:", data);
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const getAvailableSlotsForSelectedDate = () => {
    const availableSlotsForSelectedDate = availabilities.find(
      (a) => a.date === format(selectedDate, "yyyy-MM-dd")
    );
    return availableSlotsForSelectedDate ? availableSlotsForSelectedDate.slots : [];
  };

  const handleConfirmReservation = async (time, isBreak = false) => {
    if (!reservationReason.trim()) {
      alert(t("scheduleTab.reasonRequired"));
      return;
    }
  
    try {
      setIsReserving(true);
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/";
      const requestBody = {
        coachLN,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: time,
        reason: reservationReason,
      };
  
      const res = await fetch(`${API_URL}break`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });
  
      const data = await res.json();
      if (res.ok) {
        setBreaks((prevBreaks) => [
          ...prevBreaks,
          { time: time, reason: reservationReason },
        ]);
        fetchConsultations(selectedDate);
        fetchAvailabilities();
        setReservingSlot(null);
        setReservationReason("");
      } else {
        console.error("Error reserving slot:", data);
      }
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setIsReserving(false);
    }
  };

const deleteAllAvailabilitiesForSelectedDate = async () => {
  const dateStr = format(selectedDate, 'yyyy-MM-dd'); 
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || "http://localhost:8000/"}availabilities/delete-by-date/${coachLN}/${dateStr}`,
      { method: "DELETE" }
    );
    const data = await response.json();
    if (response.ok) {
      alert(`✨ Disponibilités pour ${dateStr} supprimé!`);
      fetchAvailabilities();
    } else {
      console.error("Error deleting:", data);
      alert("Oops, could not delete availabilities. Check console 💔");
    }
  } catch (err) {
    console.error(err);
    alert("Server error. Try again later! ☠️");
  }
};
  
  const fetchBreaks = async (date) => {
    const formattedDate = format(date, "yyyy-MM-dd");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000/"}breaks/coach/${coachLN}/${formattedDate}`
      );
      const data = await res.json();
      if (res.ok) {
        setBreaks(data);
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const getAllEventsForSelectedDate = () => {
    const allEvents = [];
  
    consultations.forEach((c) => {
      const type = consultationTypes[c.consultationType];
      allEvents.push({
        type: 'consultation',
        time: c.time,
        firstName: c.firstName,
        lastName: c.lastName,
        email: c.email,
        phone: c.phone,
        consultationType: type ? type.name[i18n.language] : "Unknown type",
        realDuration: type ? type.realDuration : "",
      });
    });
  
    breaks.forEach((b) => {
      allEvents.push({
        type: 'break',
        time: b.time,
        reason: b.reason,
      });
    });
  
    return allEvents.sort((a, b) => {
      const timeA = new Date(`1970-01-01T${a.time}:00`);
      const timeB = new Date(`1970-01-01T${b.time}:00`);
      return timeA - timeB;
    });
  };

 const handleMakeAvailable = async (coachLN, date, time) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:8000/"}add-break-to-avail`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ coachLN, date, time }),
    });
    if (response.ok) {
      setBreaks(prevBreaks => prevBreaks.filter(b => b.time !== time));
      fetchAvailabilities();
    } else {
      console.error('Failed to convert break');
    }
  } catch (err) {
    console.error(err);
  }
};
  useEffect(() => {
    if (coachLN) {
      fetchConsultations(selectedDate);
      fetchAvailabilities();
      fetchConsultationTypes();
      fetchBreaks(selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, coachLN]);

  const cancelReservation = () => {
    setReservingSlot(null);
    setReservationReason("");
    setIsReserving(false);
  };
  const saveConsultationNote = async (consultationId, note) => {
    try {
      // Check if the note is empty
      const url = `${process.env.REACT_APP_API_URL || "http://localhost:8000/"}consultations/${consultationId}/notes`;
  
      if (note === "") {
        // If the note is empty, send a DELETE request to remove the note from the database
        const res = await fetch(url, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (res.ok) {
          fetchConsultations(selectedDate);
        } else {
          console.error("Error deleting note:", data);
        }
      } else {
        // If the note is not empty, update the note in the database
        const res = await fetch(url, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ notes: note }),
        });
        const data = await res.json();
        if (res.ok) {
          fetchConsultations(selectedDate);
        } else {
          console.error("Error saving note:", data);
        }
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const handleDeleteNote = async (consultationId) => {
    try {
      const url = `${process.env.REACT_APP_API_URL || "http://localhost:8000/"}consultations/${consultationId}/notes`;
  
      const res = await fetch(url, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
  
      const data = await res.json();
  
      if (res.ok) {
        const updatedConsultations = consultations.map(consultation => {
          if (consultation._id === consultationId) {
            consultation.notes = null;
          }
          return consultation;
        });
        
        setConsultations(updatedConsultations);
      } else {
        console.error("Error deleting note:", data);
      }
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const hasAvailForSelected = availabilities.some(
  (a) => a.date === format(selectedDate, "yyyy-MM-dd")
);
  
  return (
    <Wrapper>
      <Title>{t("scheduleTab.title")}</Title>
      <Content>
      <LeftPanel>
  <Subtitle>
    {t("scheduleTab.scheduledFor", { date: format(selectedDate, "PPP") })}
  </Subtitle>

  {getAllEventsForSelectedDate().length === 0 ? (
    <NoConsultations>{t("scheduleTab.noConsultations")}</NoConsultations>
  ) : (
    <ConsultationList>
      {getAllEventsForSelectedDate().map((event, index) => {
        if (event.type === 'consultation') {
          return (
            <ConsultationItem key={index}>
  <strong>
    {event.time} {event.consultationType}{" "}
    {event.realDuration && <span> ({event.realDuration} min)</span>}
  </strong>{" "}
  – {event.firstName} {event.lastName} ✉️ {event.email} 📞{event.phone}

  {editingNoteIndex === index ? (
    <>
      <NoteTextarea
        value={consultationNotes[index] || ""}
        onChange={(e) =>
          setConsultationNotes({ ...consultationNotes, [index]: e.target.value })
        }
        placeholder={t("scheduleTab.notePlaceholder")}
      />
      <SaveNoteButton
  onClick={() => {
    const consultation = consultations[index];
    const note = consultationNotes[index] || "";
    saveConsultationNote(consultation._id, note);
    setEditingNoteIndex(null);
  }}
>
  Save Note
</SaveNoteButton>
    </>
  ) : (
    <>
      {consultations[index]?.notes ? (
  <NoteDisplay>
    📝 {consultations[index].notes}
    <DeleteNoteButton onClick={() => handleDeleteNote(consultations[index]._id)}>
      <Trashimg src={Trash} alt="delete note"/>
    </DeleteNoteButton>
  </NoteDisplay>
) : (
  <span></span>
)}
      <EditNoteButton onClick={() => setEditingNoteIndex(index)}>
        Add/Edit Note
      </EditNoteButton>
    </>
  )}
</ConsultationItem>
          );
        }

        return (
          <BreakItem key={index}>
            <strong>{event.time}</strong> – {event.reason}
            <MakeAvailableButton onClick={() => handleMakeAvailable(coachLN, format(selectedDate, "yyyy-MM-dd"), event.time)}>
              {t("scheduleTab.makeAvail")}
            </MakeAvailableButton>
          </BreakItem>
        );
      })}
    </ConsultationList>
  )}
</LeftPanel>

        <RightPanel>
          <StyledCalendar
            onChange={setSelectedDate}
            value={selectedDate}
            locale={i18n.language === "fr" ? "fr-FR" : "en-US"}
            tileContent={({ date, view }) =>
              view === "month" &&
              availabilities.some((a) => a.date === format(date, "yyyy-MM-dd")) ? (
                <Circle />
              ) : null
            }
            tileClassName={({ date }) => (isToday(date) ? 'today' : '')}
          />
          <Subtitle>{t("scheduleTab.availableSlots")}</Subtitle>

          <SlotList>
  {getAvailableSlotsForSelectedDate().map((slot, index) => (
    <SlotItem key={index}>
      {slot.time}
      {reservingSlot === slot.time ? (
        <ReserveForm>
          <ReasonInput
            value={reservationReason}
            onChange={(e) => setReservationReason(e.target.value)}
            placeholder={t("scheduleTab.reasonPlaceholder")}
          />
          <ConfirmButton onClick={() => handleConfirmReservation(slot.time, true)}>
  {isReserving ? t("scheduleTab.reserving") : t("scheduleTab.reserving")}
</ConfirmButton>

<CancelButton onClick={() => cancelReservation()}>
  {t("scheduleTab.cancelReservation")}
</CancelButton>
        </ReserveForm>
      ) : (
        <ReserveButton onClick={() => setReservingSlot(slot.time)}>
          {t("scheduleTab.reserve")}
        </ReserveButton>
      )}
    </SlotItem>
  ))}
 {hasAvailForSelected && (
        <DeleteButton onClick={deleteAllAvailabilitiesForSelectedDate}>
          <img src={Trash} alt="Trash icon" style={{ width: '20px' }} />
          {t("scheduleTab.deleteFor", { date: format(selectedDate, "PPP") })}
        </DeleteButton>
      )}
</SlotList>
        </RightPanel>
      </Content>
    </Wrapper>
  );
};

export default ScheduleTab;


const Wrapper = styled.div`
  max-width: 1100px;
  margin: auto;
  padding: 1rem;
  margin-top: -30px;
`;

const Title = styled.h2`
  font-size: 35px;
  color: #6d9886;
  margin-bottom: 1.5rem;
`;


const Subtitle = styled.h3`
  margin-bottom: 1rem;
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
  justify-content: space-between;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

const LeftPanel = styled.div`
  flex: 1;
`;

const RightPanel = styled.div`
  max-width: 300px;
`;

const StyledCalendar = styled(Calendar)`
  border: none;
  width: 100%;
  background-color:  #f7f9fc;;
  .react-calendar__tile.today {
    background-color: rgba(148,163,135,1);
    border-radius: 50%;
    border: green solid 2px;
    color: white;
  }
`;

const ConsultationList = styled.ul`
  list-style: none;
  padding: 0;
`;

const ConsultationItem = styled.li`
  background-color: rgba(186,204,171,0.5);
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 0.75rem;
  border-left: 5px solid rgba(148,163,135,1);
`;

const NoConsultations = styled.p`
  color: #999;
  font-style: italic;
`;

const Circle = styled.div`
  width: 8px;
  height: 8px;
  background-color: #6d9886;
  border-radius: 50%;
  margin: auto;
  margin-top: 4px;
`;

const SlotList = styled.ul`
  list-style: none;
  padding: 0;
`;

const SlotItem = styled.li`
  background-color: rgba(220, 220, 220, 0.7);
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
`;

const ReserveButton = styled.button`
  margin-left: 10px;
  background-color: rgba(217,121,97,1);
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(217, 90, 70, 1);
  }
`;

const ReserveForm = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
`;

const ReasonInput = styled.input`
  margin-bottom: 8px;
  padding: 6px;
  border: 1px solid #ccc;
  border-radius: 6px;
`;

const ConfirmButton = styled.button`
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 6px;
  margin-bottom: 4px;
  border-radius: 6px;
  cursor: pointer;
`;

const CancelButton = styled.button`
  background-color: #ff4d4d;
  color: white;
  padding: 6px 15px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  margin-top: 10px;

  &:hover {
    background-color: #ff3333;
  }
`
const BreakItem = styled.li`
   background-color: rgba(246,187,155,0.5);
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 0.75rem;
  border-left: 5px solid rgba(217,121,97,1);
`;
const MakeAvailableButton = styled.button`
  background-color: #a7d7c5;
  color: #333;
  border: none;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  margin-left: 8px;
  
  &:hover {
    background-color: #90cdb3;
  }
`;

const NoteTextarea = styled.textarea`
  display: block;
  width: 80%;
  margin-top: 8px;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 14px;
`;

const SaveNoteButton = styled.button`
  margin-top: 6px;
  padding: 6px 12px;
  background-color: #c084fc;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
`;

const EditNoteButton = styled.button`
  margin-left: 8px;
  padding: 4px 10px;
  font-size: 12px;
  background-color: #e2e8f0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
`;

const NoteDisplay = styled.div`
  margin-top: 6px;
  font-style: italic;
  color: #555;
  background: #f9fafb;
  padding: 6px;
  border-left: 3px solid #c084fc;
  border-radius: 4px;
`;

const DeleteNoteButton = styled.button`
  background-color: #f44336;
  color: white;
  border: none;
  padding: 2px 5px;
  cursor: pointer;
  margin-left: 10px;
  border-radius: 5px;
  
  &:hover {
    background-color: #d32f2f;
  }
`;

const Trashimg = styled.img`
width: 20px;
`

const DeleteButton = styled.button`
  background: #ff4d4f;
  color: white;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  &:hover {
    background: #d9363e;
  }
`;